import type { Ref } from 'vue';
import { watch, ref, onBeforeUnmount } from 'vue';

export type PersistScope = 'global' | 'route' | 'position';

export interface PersistPositionOptions {
  /** The host element for position-based storage keys */
  host?: HTMLElement | null;
  /** Props to identify this component uniquely within its position */
  props?: Record<string, any>;
}

export interface PersistOptions<T> {
  /** Base key for storage (will be prefixed based on scope) */
  key: string;
  /** Storage scope: global (localStorage), route (sessionStorage + path), or position (sessionStorage + stable ID) */
  scope?: PersistScope;
  /** Options for position-based scoping */
  options?: PersistPositionOptions;
  /** Custom serialization function (default: JSON.stringify) */
  serialize?: (value: T) => string;
  /** Custom deserialization function (default: JSON.parse) */
  deserialize?: (value: string) => T;
  /** Enable deep watching of nested properties (default: false). Warning: can cause performance issues */
  deep?: boolean;
  /** Debounce delay for storage writes in milliseconds (default: 150). Set to 0 to disable */
  debounceMs?: number;
}

const defaultSerialize = <T>(value: T): string => JSON.stringify(value);
const defaultDeserialize = <T>(value: string): T => JSON.parse(value);

let hasWarnedUnstableKey = false;

/**
 * Generates a simple hash from an object for use in storage keys.
 * Creates a stable identifier from prop values.
 *
 * @param obj - Object to hash (typically component props)
 * @returns A hash string
 */
const hashObject = (obj: Record<string, any>): string => {
  const str = JSON.stringify(
    obj,
    Object.keys(obj).sort((a, b) => a.localeCompare(b))
  );
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.codePointAt(i) ?? 0;
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return Math.abs(hash).toString(36);
};

/**
 * Generates a stable position-based storage key.
 *
 * Prefers stable identifiers (persistId from props or data-persist-id attribute)
 * over unstable DOM indices. Falls back to route scope if no stable ID is available.
 *
 * @param baseKey - The base storage key
 * @param host - The host element (used to find data-persist-id)
 * @param props - Component props (may contain persistId)
 * @returns A storage key string
 */
const generatePositionKey = (
  baseKey: string,
  host: HTMLElement | null | undefined,
  props?: Record<string, any>
): string => {
  if (!host) {
    if (!hasWarnedUnstableKey) {
      console.warn(
        '[usePersistentRef] No host provided for position scope, falling back to route scope'
      );
      hasWarnedUnstableKey = true;
    }
    return `${baseKey}:${globalThis.location.pathname}`;
  }

  // Chrome timing issue: dataset.persistId may not be populated during custom element setup
  // Fallback to getAttribute() for cross-browser reliability
  const explicitId =
    props?.persistId || host.dataset.persistId || host.getAttribute('data-persist-id'); // NOSONAR

  if (explicitId) {
    return `${baseKey}:${explicitId}`;
  }

  // Generate stable ID from props if available (excluding persistId itself)
  if (props && Object.keys(props).length > 0) {
    // Destructure to exclude persistId from hash calculation
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { persistId, ...propsForHash } = props;
    if (Object.keys(propsForHash).length > 0) {
      const hash = hashObject(propsForHash);
      return `${baseKey}:${hash}`;
    }
  }

  if (!hasWarnedUnstableKey) {
    console.warn(
      '[usePersistentRef] No persistId found for position scope. Using route scope instead. ' +
        'Add data-persist-id or persistId prop for stable position-based persistence.'
    );
    hasWarnedUnstableKey = true;
  }

  return `${baseKey}:${globalThis.location.pathname}`;
};

/**
 * Creates a persistent ref that survives page reloads by storing its value in browser storage.
 *
 * This composable:
 * - Hydrates the ref from storage on initialization
 * - Debounces storage writes to reduce I/O overhead
 * - Uses requestIdleCallback when available for non-blocking writes
 * - Supports shallow (default) or deep watching
 * - Automatically cleans up watchers and pending writes on unmount
 *
 * **Storage scopes:**
 * - `global`: Uses localStorage, persists across all pages and sessions
 * - `route`: Uses sessionStorage with pathname, persists within current session and route
 * - `position`: Uses sessionStorage with stable ID (data-persist-id or persistId prop)
 *
 * **Performance:**
 * - Uses shallow watching by default (only object identity changes trigger saves)
 * - Debounces writes (default 150ms) to batch rapid changes
 * - Leverages idle time when available to avoid blocking the main thread
 *
 * @template T - The type of value being persisted
 * @param sourceRef - The source ref whose value should be persisted
 * @param options - Configuration options for persistence behavior
 * @returns A new reactive ref synced with storage
 *
 * @example
 * ```vue
 * <script setup lang="ts">
 * import { ref } from 'vue';
 * import { usePersistentRef } from '@/runtime/composables/usePersistentRef';
 *
 * // Global scope - survives page reloads across all pages
 * const theme = ref('dark');
 * const persistentTheme = usePersistentRef(theme, {
 *   key: 'user-theme',
 *   scope: 'global'
 * });
 *
 * // Route scope - survives reloads within the same route
 * const scrollPosition = ref(0);
 * const persistentScroll = usePersistentRef(scrollPosition, {
 *   key: 'scroll-pos',
 *   scope: 'route'
 * });
 *
 * // Position scope - requires stable ID for proper persistence
 * const collapsed = ref(false);
 * const persistentCollapsed = usePersistentRef(collapsed, {
 *   key: 'sidebar-collapsed',
 *   scope: 'position',
 *   options: {
 *     host: hostElement,
 *     props: { persistId: 'main-sidebar' }
 *   }
 * });
 * </script>
 * ```
 */
export const usePersistentRef = <T>(sourceRef: Ref<T>, options: PersistOptions<T>): Ref<T> => {
  const {
    key: baseKey,
    scope = 'global',
    options: positionOptions,
    serialize = defaultSerialize,
    deserialize = defaultDeserialize,
    deep = false,
    debounceMs = 150
  } = options;

  const { storage, key } = resolveStorageAndKey(scope, baseKey, positionOptions);
  const persistedRef = ref(sourceRef.value) as Ref<T>;

  let isHydrating = true;
  let debounceTimer: ReturnType<typeof setTimeout> | null = null;

  hydrateFromStorage(storage, key, deserialize, persistedRef, sourceRef);

  queueMicrotask(() => {
    isHydrating = false;
  });

  const debouncedSave = createDebouncedSave(
    storage,
    key,
    serialize,
    persistedRef,
    debounceMs,
    debounceTimer
  );

  const stopWatch = watch(
    () => sourceRef.value,
    newValue => {
      if (isHydrating) return;
      debouncedSave(newValue);
    },
    { deep }
  );

  onBeforeUnmount(() => {
    stopWatch();
    if (debounceTimer !== null) {
      clearTimeout(debounceTimer);
      debounceTimer = null;
    }
  });

  return persistedRef;
};

/**
 * Resolves the appropriate storage backend and key based on the scope.
 *
 * @param scope - The persistence scope
 * @param baseKey - The base storage key
 * @param positionOptions - Options for position-based scoping
 * @returns Object containing the storage backend and final key
 */
const resolveStorageAndKey = (
  scope: PersistScope,
  baseKey: string,
  positionOptions?: PersistPositionOptions
): { storage: Storage; key: string } => {
  switch (scope) {
    case 'global':
      return { storage: localStorage, key: baseKey };
    case 'route':
      return { storage: sessionStorage, key: `${baseKey}:${globalThis.location.pathname}` };
    case 'position':
      return {
        storage: sessionStorage,
        key: generatePositionKey(baseKey, positionOptions?.host, positionOptions?.props)
      };
    default:
      return { storage: localStorage, key: baseKey };
  }
};

/**
 * Hydrates the ref from storage on initialization.
 *
 * @param storage - The storage backend
 * @param key - The storage key
 * @param deserialize - Deserialization function
 * @param persistedRef - The persisted ref to update
 * @param sourceRef - The source ref to update
 */
const hydrateFromStorage = <T>(
  storage: Storage,
  key: string,
  deserialize: (value: string) => T,
  persistedRef: Ref<T>,
  sourceRef: Ref<T>
): void => {
  try {
    const saved = storage.getItem(key);
    if (saved !== null) {
      const hydrated = deserialize(saved);
      persistedRef.value = hydrated;
      sourceRef.value = hydrated;
    }
  } catch (e) {
    console.warn(`[usePersistentRef] Failed to hydrate ${key}:`, e);
  }
};

/**
 * Creates a debounced save function for storage writes.
 *
 * Uses requestIdleCallback when available to perform writes during idle time,
 * reducing impact on main thread performance.
 *
 * @param storage - The storage backend
 * @param key - The storage key
 * @param serialize - Serialization function
 * @param persistedRef - The persisted ref to update
 * @param debounceMs - Debounce delay in milliseconds
 * @param debounceTimer - Reference to the debounce timer
 * @returns A debounced save function
 */
const createDebouncedSave = <T>(
  storage: Storage,
  key: string,
  serialize: (value: T) => string,
  persistedRef: Ref<T>,
  debounceMs: number,
  debounceTimer: ReturnType<typeof setTimeout> | null
): ((newValue: T) => void) => {
  return (newValue: T): void => {
    if (debounceTimer !== null) {
      clearTimeout(debounceTimer);
    }

    const doSave = (): void => {
      try {
        const serialized = serialize(newValue);
        storage.setItem(key, serialized);
        persistedRef.value = newValue;
      } catch (e) {
        console.warn(`[usePersistentRef] Failed to save ${key}:`, e);
      }
    };

    if (debounceMs === 0) {
      doSave();
    } else if ('requestIdleCallback' in globalThis) {
      debounceTimer = setTimeout(() => {
        requestIdleCallback(() => doSave(), { timeout: debounceMs });
      }, debounceMs) as any;
    } else {
      debounceTimer = setTimeout(doSave, debounceMs);
    }
  };
};
